import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from "react"

const ALLOWED_CHARS_REGEX = /[^A-Za-z0-9!@#$%^&*()_+\-=?.,:'"£¢`\s]/g

const DEFAULT_FONT_PX = 21

export type VisualiserTextBoxViewModelOptions = {
  font: string
  fontWeight: string
  /** Bare numbers become `Npx` and support auto-shrink; strings are applied as CSS `font-size` verbatim. */
  fontSize?: number | string
  lineHeight: number
  size?: {
    width: string
    minHeight: string
    maxHeight: string
    minWidth: string
    maxWidth: string
  }
  position?: {
    top?: string
    bottom?: string
    left?: string
    right?: string
  }
  maxLength?: number
  maxLines?: number
  initialValue: string
  userEditable: boolean
  value?: string
  color?: string
  style?: CSSProperties
  /** Overrides the browser-default placeholder colour; any opacity comes from the colour's alpha. */
  placeholderColor?: string
  setValue: (value: string) => void
}

function layoutTextElement(
  el: HTMLTextAreaElement | HTMLDivElement,
  options: {
    adjustedFontSize: number
    fontSize: number | string
    lineHeight: number
    maxLines?: number
    maxHeight?: string
    setAdjustedFontSize: (n: number) => void
  },
) {
  const { adjustedFontSize, fontSize, lineHeight, maxLines, maxHeight, setAdjustedFontSize } = options

  if (typeof fontSize === "string") {
    el.style.fontSize = fontSize
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
    return
  }

  const maxHeightValue = maxHeight ? parseFloat(maxHeight) : undefined
  let effectiveMaxHeight = maxHeightValue
  if (maxLines) {
    const computedLineHeight = lineHeight * fontSize
    const maxLinesHeight = maxLines * computedLineHeight
    if (maxHeightValue) {
      effectiveMaxHeight = Math.min(maxHeightValue, maxLinesHeight)
    } else {
      effectiveMaxHeight = maxLinesHeight
    }
  }
  const minFontSize = 8
  let optimalFontSize = fontSize
  let testFontSize = fontSize
  el.style.fontSize = `${testFontSize}px`
  el.style.height = "auto"
  const scrollHeight = el.scrollHeight
  if (effectiveMaxHeight && scrollHeight > effectiveMaxHeight) {
    while (testFontSize > minFontSize) {
      testFontSize -= 0.5
      el.style.fontSize = `${testFontSize}px`
      el.style.height = "auto"
      if (el.scrollHeight <= effectiveMaxHeight) {
        optimalFontSize = testFontSize
        break
      }
    }
    if (testFontSize <= minFontSize) {
      optimalFontSize = minFontSize
    }
  } else if (adjustedFontSize < fontSize && effectiveMaxHeight) {
    testFontSize = adjustedFontSize
    while (testFontSize < fontSize) {
      const nextSize = testFontSize + 0.5
      el.style.fontSize = `${nextSize}px`
      el.style.height = "auto"
      if (el.scrollHeight <= effectiveMaxHeight) {
        testFontSize = nextSize
      } else {
        break
      }
    }
    optimalFontSize = testFontSize
  }
  if (optimalFontSize !== adjustedFontSize) {
    setAdjustedFontSize(optimalFontSize)
  }
  el.style.fontSize = `${optimalFontSize}px`
  el.style.height = "auto"
  el.style.height = `${el.scrollHeight}px`
}

/**
 * Text measurement, sanitisation, and layout for the visualiser text box (editable textarea or static label).
 */
export function useVisualiserTextBoxViewModel(options: VisualiserTextBoxViewModelOptions) {
  const {
    font,
    fontWeight,
    fontSize: fontSizeProp,
    lineHeight,
    size,
    position,
    color,
    maxLength,
    maxLines,
    initialValue,
    userEditable,
    value,
    style: extraStyle,
    placeholderColor,
    setValue,
  } = options

  const baseFontSize = fontSizeProp ?? DEFAULT_FONT_PX

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const staticRef = useRef<HTMLDivElement>(null)
  const [adjustedFontSize, setAdjustedFontSize] = useState(() =>
    typeof baseFontSize === "number" ? baseFontSize : DEFAULT_FONT_PX,
  )

  useEffect(() => {
    if (typeof baseFontSize === "number") {
      setAdjustedFontSize(baseFontSize)
    }
  }, [baseFontSize])

  const adjustHeight = useCallback(() => {
    const el = userEditable ? textareaRef.current : staticRef.current
    if (!el) {
      return
    }
    layoutTextElement(el, {
      adjustedFontSize,
      fontSize: baseFontSize,
      lineHeight,
      maxLines,
      maxHeight: size?.maxHeight,
      setAdjustedFontSize,
    })
  }, [
    adjustedFontSize,
    baseFontSize,
    lineHeight,
    maxLines,
    size?.maxHeight,
    userEditable,
  ])

  useEffect(() => {
    adjustHeight()
  }, [adjustHeight, value, baseFontSize, size?.maxHeight, maxLines, lineHeight, userEditable])

  useEffect(() => {
    if (!userEditable) {
      return
    }
    if (value === undefined) {
      setValue(initialValue)
    }
  }, [])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      let newValue = e.target.value
      newValue = newValue.replace(ALLOWED_CHARS_REGEX, "")
      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength)
      }
      if (maxLines) {
        const lines = newValue.split("\n")
        if (lines.length > maxLines) {
          newValue = lines.slice(0, maxLines).join("\n")
        }
      }
      setValue(newValue)
    },
    [maxLength, maxLines, setValue],
  )

  const displayValue = value ?? ""

  const fontSizeCss: string =
    typeof baseFontSize === "number" ? `${adjustedFontSize}px` : baseFontSize

  const textareaStyle: CSSProperties = {
    position: "absolute",
    top: position?.top,
    bottom: position?.bottom,
    left: position?.left,
    right: position?.right,
    width: size?.width || "100%",
    minWidth: size?.minWidth,
    maxWidth: size?.maxWidth,
    minHeight: size?.minHeight,
    maxHeight: size?.maxHeight,
    fontFamily: font,
    fontSize: fontSizeCss,
    fontWeight: fontWeight,
    // A custom placeholder colour carries its own alpha, so the empty-state dim would double up.
    opacity: userEditable ? (!value && !placeholderColor ? 0.5 : 1) : 1,
    lineHeight: lineHeight,
    color: color || "var(--color-black)",
    padding: font === "Amalfi Coast" ? "8px 0 0 0" : "0",
    ...(userEditable ? {} : { whiteSpace: "pre-wrap" as const }),
    ...(placeholderColor
      ? ({ "--visualiser-placeholder-color": placeholderColor } as CSSProperties)
      : {}),
    ...extraStyle,
  }

  return {
    textareaRef,
    staticRef,
    handleChange,
    textareaStyle,
    userEditable,
    placeholder: initialValue,
    displayValue,
  }
}
